import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/auth-provider";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  getDocumentByType,
  listSections,
  recordManualExport,
  type DocumentSectionRow,
  type DocumentType,
} from "@/lib/api/manuals";
import { DocHomeLink } from "@/pages/tenant/doc-nav";
import { countInlineGaps, manualCompleteness } from "@argus/engine";

/**
 * Authored-document package — the print-clean deliverable for the compliance
 * manual or the Form ADV Part 2A brochure. Honest by construction: only
 * applicable, drafted sections are assembled; the cover states which sections
 * still need CCO input and that the document is a drafting aid for review with
 * counsel, not an adopted/filed document. Browser print-to-PDF is the export;
 * JSON gives a machine-readable handoff.
 */
function PackageInner({ docType }: { docType: DocumentType }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const isBrochure = docType === "adv_2a_brochure";

  const { data: doc } = useQuery({
    queryKey: ["doc", docType, tenant.id],
    queryFn: () => getDocumentByType(tenant.id, docType),
  });
  const { data: sections } = useQuery({
    queryKey: ["doc-sections", doc?.id],
    queryFn: () => listSections(doc?.id ?? ""),
    enabled: !!doc,
  });

  if (!doc) return <p className="text-sm text-neutral-500">No document to package yet.</p>;
  const d = doc;
  const rows = sections ?? [];
  const included = rows
    .filter((s) => s.applicability !== "not_applicable" && s.body?.trim())
    .sort((a, b) => a.sort_order - b.sort_order);
  const completeness = manualCompleteness(
    rows.map((s) => ({
      applicability: s.applicability,
      source: s.source,
      body: s.body,
      reviewStatus: s.review_status,
    })),
  );
  const inputsNeeded = included.filter((s) => countInlineGaps(s.body) > 0);

  const docTitle = isBrochure
    ? "Form ADV Part 2A Brochure"
    : "Compliance Policies & Procedures Manual";
  const ruleNote = isBrochure
    ? "Form ADV Part 2A; it is not legal advice and should be reviewed with counsel before filing via IARD."
    : "Advisers Act Rule 206(4)-7; it is not legal advice and should be reviewed with counsel before adoption.";

  function exportJson() {
    const payload = {
      doc_type: d.doc_type,
      title: d.title,
      status: d.status,
      version: d.current_version,
      effective_date: d.effective_date,
      engine_version: d.engine_version,
      firm_profile: d.firm_profile,
      generated_at: new Date().toISOString(),
      tenant: tenant.name,
      completeness: {
        applicable: completeness.applicable,
        drafted: completeness.drafted,
        reviewedPct: completeness.reviewedPct,
      },
      sections: included.map((s) => ({
        section_key: s.section_key,
        heading: s.heading,
        reg_ref: s.reg_ref,
        body: s.body,
        source: s.source,
        review_status: s.review_status,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${isBrochure ? "adv-2a-brochure" : "compliance-manual"}-${tenant.slug}-v${d.current_version || "draft"}.json`;
    a.click();
    URL.revokeObjectURL(url);
    if (user) void recordManualExport(d, user.id);
  }

  return (
    <div className="print:text-black">
      <header className="flex items-start justify-between print:hidden">
        <div>
          <DocHomeLink
            docType={docType}
            slug={tenant.slug}
            className="text-xs text-neutral-500 hover:text-neutral-300"
          >
            ← Back to {isBrochure ? "brochure" : "manual"}
          </DocHomeLink>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isBrochure ? "Brochure" : "Manual"} package
          </h1>
          <p className="text-sm text-neutral-400">
            Print-ready {isBrochure ? "Part 2A brochure" : "compliance manual"}. Use your
            browser&rsquo;s print dialog → &ldquo;Save as PDF&rdquo;, or export JSON for a
            machine-readable handoff.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportJson}
            className="rounded border border-neutral-700 px-4 py-2 text-sm font-medium text-neutral-200 hover:bg-neutral-800"
          >
            Export JSON
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-white"
          >
            Print / Save as PDF
          </button>
        </div>
      </header>

      <article className="mt-4 space-y-6 rounded border border-neutral-800 bg-white p-8 text-neutral-900 print:mt-0 print:rounded-none print:border-0 print:p-0">
        <header className="border-b border-neutral-300 pb-4">
          <h1 className="text-2xl font-bold">{tenant.name}</h1>
          <p className="mt-1 text-lg font-semibold text-neutral-700">{docTitle}</p>
          <p className="mt-1 text-sm text-neutral-600">
            {d.current_version > 0 ? `Version ${d.current_version}` : "Draft"}
            {d.effective_date ? ` · Effective ${d.effective_date}` : ""} ·{" "}
            <span className="capitalize">{d.status.replace(/_/g, " ")}</span>
          </p>
          <p className="mt-2 text-xs text-neutral-500">
            Generated {new Date().toLocaleString()} by Argus ({d.engine_version}). This document is
            a <strong>drafting aid</strong> tailored from the firm profile under {ruleNote}{" "}
            Bracketed [CCO: …] markers indicate firm-specific facts to be supplied.
          </p>
        </header>

        {/* Table of contents */}
        <section>
          <h2 className="text-lg font-semibold">Contents</h2>
          <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-sm">
            {included.map((s) => (
              <li key={s.id}>
                {s.heading}
                {s.reg_ref ? <span className="text-neutral-500"> — {s.reg_ref}</span> : ""}
              </li>
            ))}
          </ol>
          {inputsNeeded.length > 0 && (
            <p className="mt-3 text-xs text-neutral-600">
              {inputsNeeded.length} section(s) contain bracketed [CCO: …] inputs still to be
              supplied before adoption.
            </p>
          )}
        </section>

        {included.map((s, i) => (
          <PackagedSection key={s.id} index={i + 1} section={s} />
        ))}

        {included.length === 0 && (
          <p className="text-sm text-neutral-600">
            No applicable sections have been drafted yet. Return to the overview and use “Draft with
            AI”.
          </p>
        )}
      </article>
    </div>
  );
}

function PackagedSection({ index, section }: { index: number; section: DocumentSectionRow }) {
  const parts = (section.body ?? "").split(/(\[CCO:[^\]]*\])/g);
  return (
    <section className="break-inside-avoid">
      <h2 className="text-lg font-semibold">
        {index}. {section.heading}
      </h2>
      {section.reg_ref && <p className="text-xs text-neutral-500">{section.reg_ref}</p>}
      <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
        {parts.map((part, i) =>
          part.startsWith("[CCO:") ? (
            <mark key={i} className="bg-amber-100 px-0.5 text-amber-900">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          ),
        )}
      </div>
    </section>
  );
}

export function ManualPackagePage({ docType = "compliance_manual" }: { docType?: DocumentType }) {
  return (
    <TenantLayout>
      <PackageInner docType={docType} />
    </TenantLayout>
  );
}
